/**
 * Candidate pools API (imports, ACL, Google Sheets links).
 */

import { useAuth } from '../hooks/useAuth';

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

function getAuthToken(): string | null {
  const authData = localStorage.getItem('cd-people-auth');
  if (authData) {
    try {
      const { state } = JSON.parse(authData);
      return state.token || null;
    } catch {
      return null;
    }
  }
  return null;
}

function handle401(response: Response) {
  if (response.status === 401) {
    useAuth.getState().logout();
  }
}

function jsonHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  return headers;
}

function authHeaders(): HeadersInit {
  const headers: HeadersInit = {};
  const token = getAuthToken();
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface PoolWithRole {
  id: number;
  name: string;
  description: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  role: string;
  is_internal_directory?: boolean;
}

/** Canonical review statuses (matches `people.review_status` on the backend). */
export type PersonReviewStatus = 'unreviewed' | 'needs_review' | 'reviewed';

/** Top-skill summary attached to a pool member row (Phase 3B). */
export interface PoolMemberTopSkill {
  skill_id: string;
  name: string;
  confidence: number;
}

export interface PoolMember {
  id: number;
  pool_id: number;
  person_id: number;
  added_at: string;
  added_by: string;
  source_kind: string;
  source_metadata: Record<string, unknown> | null;
  person_name?: string;
  person_title?: string;
  person_contact?: string;
  // Phase 3B enrichment (optional — older backends and legacy rows may omit).
  person_primary_location?: string | null;
  person_seniority_level?: string | null;
  person_review_status?: PersonReviewStatus | null;
  person_updated_at?: string | null;
  top_skills?: PoolMemberTopSkill[];
  total_skills_count?: number;
}

/** Pools the current user can read where this person appears (from GET /api/people/:id/pool-memberships). */
export interface PersonPoolMembership {
  pool_id: number;
  pool_name: string;
  is_internal_directory?: boolean;
  my_role: string;
  added_at: string;
  added_by: string;
  source_kind: string;
  person_id: number;
}

export interface PoolUser {
  id: number;
  pool_id: number;
  user_email: string;
  role: string;
  created_at: string;
}

export interface SheetLink {
  id: number;
  pool_id: number;
  spreadsheet_id: string;
  sheet_range: string | null;
  connected_by_email: string;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export async function listPools(): Promise<PoolWithRole[]> {
  const res = await fetch(`${API_BASE}/candidate-pools`, { headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to list pools');
  const data = await res.json();
  return data.pools || [];
}

export async function createPool(name: string, description?: string): Promise<number> {
  const res = await fetch(`${API_BASE}/candidate-pools`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name, description: description ?? null }),
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to create pool');
  return data.id;
}

export async function getPool(poolId: number): Promise<{ pool: PoolWithRole; role: string }> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}`, { headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) throw new Error('Pool not found');
  return res.json();
}

export async function updatePool(poolId: number, body: { name?: string; description?: string | null }): Promise<void> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  handle401(res);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Update failed');
}

export async function deletePool(poolId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}`, { method: 'DELETE', headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Delete failed');
}

export async function listPoolUsers(poolId: number): Promise<PoolUser[]> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/users`, { headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) throw new Error('Failed to list users');
  const data = await res.json();
  return data.users || [];
}

export async function addPoolUser(poolId: number, email: string, role: string): Promise<void> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/users`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, role }),
  });
  handle401(res);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to add user');
}

export async function removePoolUser(poolId: number, email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  handle401(res);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to remove user');
}

export async function updatePoolUserRole(poolId: number, email: string, role: string): Promise<void> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ role }),
  });
  handle401(res);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to update role');
}

export async function listMembers(poolId: number): Promise<PoolMember[]> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/members`, { headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) throw new Error('Failed to list members');
  const data = await res.json();
  return data.members || [];
}

export interface ManualPoolMemberResult {
  person_id: number;
  created: boolean;
  updated_existing: boolean;
}

export class ManualPoolMemberApiError extends Error {
  status: number | null;
  field?: string;
  constructor(message: string, status: number | null, field?: string) {
    super(message);
    this.name = 'ManualPoolMemberApiError';
    this.status = status;
    this.field = field;
  }
}

export async function createManualPoolMember(
  poolId: number,
  payload: Record<string, unknown>
): Promise<ManualPoolMemberResult> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/members/manual`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ManualPoolMemberApiError(
      data.error || 'Failed to add person',
      res.status,
      typeof data.field === 'string' ? data.field : undefined
    );
  }
  return data as ManualPoolMemberResult;
}

export interface RemovePoolMemberResult {
  ok: boolean;
  removed_from_pool_id: number;
  archived: boolean;
  remaining_recruiting_pools: number;
}

export async function removeMember(
  poolId: number,
  personId: number
): Promise<RemovePoolMemberResult> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/members/${personId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to remove member');
  return data as RemovePoolMemberResult;
}

export async function listPoolMembershipsForPerson(personId: number): Promise<PersonPoolMembership[]> {
  const res = await fetch(`${API_BASE}/people/${personId}/pool-memberships`, { headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || 'Failed to list pool memberships');
  }
  const data = await res.json();
  return data.memberships || [];
}

/**
 * Summary fields surfaced to the UI after a successful import. Mirrors the
 * backend `PersistResult` shape but picks only what the toast and highlight
 * flow need. The endpoint returns the full object; callers can widen the
 * type if they need more later.
 */
export interface ImportResult {
  person_id: number;
  persist?: {
    personId: number;
    applied: {
      name?: string;
      title?: string;
      primary_location?: string;
      seniority_level?: string;
      years_experience_total?: number;
      professional_summary?: string;
      contact?: string;
    };
    experience_rows: number;
    project_rows: number;
    education_rows: number;
    certification_rows: number;
    language_rows: number;
    target_role_rows: number;
    domain_rows: number;
    industry_rows: number;
    skill_rows: number;
    skill_phrase_rows: number;
    search_blob_chars: number;
    review_status: 'needs_review' | 'unreviewed' | 'reviewed';
    parser_version: string;
    extraction_run_id: string;
  };
}

/** Thrown by import endpoints so the caller can classify the HTTP status. */
export class ImportApiError extends Error {
  status: number | null;
  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'ImportApiError';
    this.status = status;
  }
}

export async function importPaste(
  poolId: number,
  text: string,
  opts: { signal?: AbortSignal } = {}
): Promise<ImportResult> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/import/paste`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ text }),
    signal: opts.signal,
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ImportApiError(data.error || 'Import failed', res.status);
  return data as ImportResult;
}

export async function importCv(
  poolId: number,
  file: File,
  opts: { signal?: AbortSignal; personId?: number } = {}
): Promise<ImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  if (opts.personId != null && opts.personId > 0) {
    fd.append('person_id', String(opts.personId));
  }
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/import/cv`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
    signal: opts.signal,
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ImportApiError(data.error || 'CV import failed', res.status);
  return data as ImportResult;
}

export async function importBulk(poolId: number, file: File): Promise<{ created: number; skipped: number; errors: string[] }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/import/bulk`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Bulk import failed');
  return data;
}

export async function getSheetsOAuthUrl(
  poolId: number,
  spreadsheetId: string,
  sheetRange?: string | null
): Promise<string> {
  const q = new URLSearchParams({ spreadsheetId });
  if (sheetRange) q.set('sheetRange', sheetRange);
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/sheets/oauth-url?${q}`, {
    headers: jsonHeaders(),
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to get OAuth URL');
  return data.url;
}

export async function listSheetLinks(poolId: number): Promise<SheetLink[]> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/sheets/links`, { headers: jsonHeaders() });
  handle401(res);
  if (!res.ok) throw new Error('Failed to list sheet links');
  const data = await res.json();
  return data.links || [];
}

export async function deleteSheetLink(poolId: number, linkId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/sheets/links/${linkId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  handle401(res);
  if (!res.ok) throw new Error('Failed to delete link');
}

export async function syncSheet(poolId: number, linkId: number): Promise<{ created: number; skipped: number; errors: string[] }> {
  const res = await fetch(`${API_BASE}/candidate-pools/${poolId}/sheets/sync`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ linkId }),
  });
  handle401(res);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Sync failed');
  return data;
}
